class SpendSaveProducts extends HTMLElement {
  connectedCallback() {
    const firstPage = new URL(location.href);
    if (Number(firstPage.searchParams.get('page')) > 1) {
      firstPage.searchParams.delete('page');
      location.replace(firstPage);
      return;
    }
    this.abortController = new AbortController();
    this.onSectionLoad = () => this.observeMainBanner();
    document.addEventListener('shopify:section:load', this.onSectionLoad);
    this.observeMainBanner();
    this.tabs = [...this.querySelectorAll('[role="tab"]')];
    this.panels = [...this.querySelectorAll('[data-panel]')];
    this.pageSize = Number(this.dataset.pageSize);
    this.panels.forEach((panel) => {
      const cards = [...panel.querySelectorAll('[data-product-id]')];
      panel.cards = cards;
      panel.visibleCount = this.pageSize;
      panel.nextUrl = panel.querySelector('[data-pagination]')?.dataset.nextUrl;
      panel.complete = !panel.nextUrl;
      this.updateCount(panel);
      this.updateProducts(panel);
    });
    this.onClick = (event) => {
      const tab = event.target.closest('[role="tab"]');
      if (tab && this.contains(tab)) this.select(tab.dataset.group, true);
      const more = event.target.closest('[data-more]');
      if (more && this.contains(more)) {
        const panel = more.closest('[data-panel]');
        if (panel.cards.length <= panel.visibleCount) { this.loadRemaining(panel); return; }
        const nextCard = panel.cards[panel.visibleCount];
        panel.visibleCount += this.pageSize;
        this.updateProducts(panel);
        nextCard?.querySelector('a')?.focus({ preventScroll: true });
      }
    };
    this.onKeydown = (event) => {
      const tab = event.target.closest('[role="tab"]');
      if (!tab || !this.contains(tab)) return;
      let index = this.tabs.indexOf(tab);
      if (event.key === 'ArrowRight') index = (index + 1) % this.tabs.length;
      else if (event.key === 'ArrowLeft') index = (index - 1 + this.tabs.length) % this.tabs.length;
      else if (event.key === 'Home') index = 0;
      else if (event.key === 'End') index = this.tabs.length - 1;
      else return;
      event.preventDefault();
      this.tabs[index].focus();
      this.select(this.tabs[index].dataset.group, true);
    };
    this.onPopstate = () => this.syncFromUrl();
    this.addEventListener('click', this.onClick);
    this.addEventListener('keydown', this.onKeydown);
    window.addEventListener('popstate', this.onPopstate);
    this.syncFromUrl();
    this.panels.forEach((panel) => this.loadRemaining(panel));
  }

  disconnectedCallback() {
    this.abortController?.abort();
    this.bannerResizeObserver?.disconnect();
    document.removeEventListener('shopify:section:load', this.onSectionLoad);
    this.style.removeProperty('--rm-category-banner-height');
    this.removeEventListener('click', this.onClick);
    this.removeEventListener('keydown', this.onKeydown);
    window.removeEventListener('popstate', this.onPopstate);
  }

  observeMainBanner() {
    this.bannerResizeObserver?.disconnect();
    const mainBanner = document.querySelector('main .rm-spend-banner');
    if (!mainBanner) return;
    const syncBannerHeight = () => {
      const height = mainBanner.getBoundingClientRect().height;
      if (height > 0) this.style.setProperty('--rm-category-banner-height', `${height}px`);
    };
    syncBannerHeight();
    this.bannerResizeObserver = new ResizeObserver(syncBannerHeight);
    this.bannerResizeObserver.observe(mainBanner);
  }

  updateCount(panel) {
    const noun = panel.dataset.panel === 'packages' ? 'packages' : 'items';
    panel.querySelector('[data-count]').textContent = `${panel.cards.length} ${noun}${panel.complete ? '' : ' loaded'}`;
  }

  async loadRemaining(panel) {
    if (panel.loading || panel.complete) return;
    let next = panel.nextUrl;
    const seenPages = new Set();
    panel.failed = false;
    panel.loading = true;
    this.updateProducts(panel);
    try {
      while (next) {
        const url = new URL(location.href);
        const paginationUrl = new URL(next, location.origin);
        if (paginationUrl.origin !== location.origin) throw new Error('Invalid pagination origin');
        paginationUrl.searchParams.forEach((value, key) => url.searchParams.set(key, value));
        url.searchParams.set('section_id', this.dataset.sectionId);
        if (seenPages.has(url.href)) throw new Error('Repeated pagination page');
        seenPages.add(url.href);
        const response = await fetch(url, { signal: this.abortController.signal });
        if (!response.ok) throw new Error('Unable to load products');
        const document = new DOMParser().parseFromString(await response.text(), 'text/html');
        const incoming = document.querySelector(`[data-panel="${panel.dataset.panel}"]`);
        if (!incoming?.querySelector('[data-pagination]')) throw new Error('Missing product panel');
        const ids = new Set(panel.cards.map((card) => card.dataset.productId));
        incoming.querySelectorAll('[data-product-id]').forEach((card) => {
          if (!ids.has(card.dataset.productId)) {
            ids.add(card.dataset.productId);
            card.hidden = true;
            panel.querySelector('[data-products]').append(card);
            panel.cards.push(card);
          }
        });
        next = incoming.querySelector('[data-pagination]').dataset.nextUrl;
        panel.nextUrl = next;
        this.updateCount(panel);
        this.updateProducts(panel);
      }
      panel.complete = true;
      this.updateCount(panel);
    } catch (error) {
      if (error.name === 'AbortError') return;
      this.updateCount(panel);
      panel.failed = true;
    } finally {
      panel.loading = false;
      this.updateProducts(panel);
    }
  }

  updateProducts(panel) {
    panel.cards.forEach((card, index) => { card.hidden = index >= panel.visibleCount; });
    const more = panel.querySelector('[data-more]');
    const hasHiddenCards = panel.cards.length > panel.visibleCount;
    more.hidden = !hasHiddenCards && panel.complete;
    more.disabled = !hasHiddenCards && panel.loading;
    more.textContent = !hasHiddenCards && panel.failed ? 'Try loading more' : 'Show more';
  }

  syncFromUrl() {
    this.select(new URLSearchParams(location.search).get('group') === 'packages' ? 'packages' : 'singles');
  }

  select(group, updateHistory = false) {
    this.tabs.forEach((tab) => {
      const selected = tab.dataset.group === group;
      tab.setAttribute('aria-selected', String(selected));
      tab.tabIndex = selected ? 0 : -1;
    });
    this.panels.forEach((panel) => { panel.hidden = panel.dataset.panel !== group; });
    if (updateHistory) {
      const url = new URL(location.href);
      url.searchParams.delete('page');
      if (group === 'packages') url.searchParams.set('group', group);
      else url.searchParams.delete('group');
      history.pushState(null, '', url);
    }
  }
}

if (!customElements.get('spend-save-products')) {
  customElements.define('spend-save-products', SpendSaveProducts);
}
